import {EventEmitter} from "nate-react-api-helpers";
import {useEffect, useState} from "react";
import {grey} from "@mui/material/colors";
import WarningIcon from '@mui/icons-material/Warning';
import {hollowMetalDoor, woodDoor} from "./openings/Const";
import {QuotePriceLine} from "../../../api/QuotePricing";

export const priceDetailEmitter = new EventEmitter<QuotePriceLine[]>();
priceDetailEmitter.emit([]);

export type PriceDetail = {
    frames: number;
    doors: number;
    doorsDetail: {
        hm: { total: number; unpriced: number };
        wd: { total: number; unpriced: number };
        other: { total: number; unpriced: number };
    }
    div10: number;
    glazing: number;
    hardware: number;
    hardwareDetail: {
        category: number;
        name: string;
        sortOrder: number;
        total: number;
        unpriced: number;
    }[];
    div10Detail: {
        "wr-accessory": { total: number; unpriced: number };
        "wr-partition": { total: number; unpriced: number };
        "locker": { total: number; unpriced: number };
        "mailbox": { total: number; unpriced: number };
        "corner-guard": { total: number; unpriced: number };
    }
    misc: number;
}

export function useUnPricedItems() {
    const [pending, setPending] = useState(0);
    const [details, setDetails] = useState<PriceDetail>({
        frames: 0,
        doors: 0,
        doorsDetail: {
            hm: {unpriced: 0, total: 0},
            wd:{unpriced: 0, total: 0},
            other: {unpriced: 0, total: 0},
        },
        div10: 0,
        glazing: 0,
        hardware: 0,
        hardwareDetail: [],
        div10Detail: {
            "wr-accessory": {unpriced: 0, total: 0},
            "wr-partition": {unpriced: 0, total: 0},
            "locker": {unpriced: 0, total: 0},
            "mailbox": {unpriced: 0, total: 0},
            "corner-guard": {unpriced: 0, total: 0},
        },
        misc: 0,
    })

    useEffect(() => {
        const isPending = (r: TRow) => {
            if(r.qty === 0) return false;
            return !r.extendedPrice
        };

        type TRows = QuotePriceLine[]
        type TRow = QuotePriceLine

        const filterRows = (rows: TRows, condition: (input: TRow) => boolean) => rows.filter(r => condition(r) && isPending(r)).length;
        const filterRows2 = (rows: TRows, condition: (input: TRow) => boolean) => {
            return {
                total: rows.filter(condition).length,
                unpriced: rows.filter(r => condition(r) && isPending(r)).length,
            }
        };

        const sub = priceDetailEmitter.subscribeAndFireLast(rows => {
            const doorRows = (r: TRow) => r.productType === "door" || r.productType === "door-prep";
            const frameRows = (r: TRow) => r.productType === "frame" || r.productType === "frame-anchor" || r.productType === "frame-prep"
            const div10Rows = (r: TRow, category: string) => r.productType === "div-10" && (r.div10?.category || "wr-accessory") === category;

            // group by-us hardware lines by their root supplier category so each
            // becomes a subtab with its own priced/unpriced counts.
            const hardwareByCat = new Map<number, {category: number; name: string; sortOrder: number; total: number; unpriced: number}>();
            rows.forEach(r => {
                if(r.productType !== "hardware") return;
                if(r.byOthers === true) return;
                if(r.rootSupplierCategory == null) return;

                const id = r.rootSupplierCategory;
                let entry = hardwareByCat.get(id);
                if(!entry) {
                    entry = {category: id, name: r.rootSupplierCategoryName || "Hardware", sortOrder: r.rootSupplierCategorySortOrder ?? 0, total: 0, unpriced: 0};
                    hardwareByCat.set(id, entry);
                }

                entry.total++;
                if(isPending(r)) entry.unpriced++;
            });
            const hardwareDetail = Array.from(hardwareByCat.values()).sort((a, b) => a.sortOrder - b.sortOrder);

            setDetails({
                doors: filterRows(rows, doorRows),
                doorsDetail: {
                    hm: filterRows2(rows, r => doorRows(r) && (r.doorSeries || r.door?.series) === hollowMetalDoor),
                    wd: filterRows2(rows, r => doorRows(r) && (r.doorSeries || r.door?.series) === woodDoor),
                    other: filterRows2(rows, r => doorRows(r) && (r.doorSeries || r.door?.series) !== hollowMetalDoor && (r.doorSeries || r.door?.series) !== woodDoor),
                },
                frames: filterRows(rows, frameRows),
                div10: filterRows(rows, r => r.productType === "div-10" && isPending(r)),
                div10Detail: {
                    "wr-accessory": filterRows2(rows, r => div10Rows(r, "wr-accessory")),
                    "wr-partition": filterRows2(rows, r => div10Rows(r, "wr-partition")),
                    "locker": filterRows2(rows, r => div10Rows(r, "locker")),
                    "mailbox": filterRows2(rows, r => div10Rows(r, "mailbox")),
                    "corner-guard": filterRows2(rows, r => div10Rows(r, "corner-guard")),
                },
                glazing: filterRows(rows, r => r.productType === "glazing" && isPending(r)),
                hardware: filterRows(rows, r => r.productType === "hardware" && isPending(r)),
                hardwareDetail: hardwareDetail,
                misc: filterRows(rows, r => r.productType === "misc" && isPending(r)),
            });

            setPending(filterRows(rows, r => isPending(r)));
        });

        return () => sub.cancel();
    }, []);

    return {
        pending,
        details
    }
}

export function UnPricedItems() {
    const {pending} = useUnPricedItems();
    if(!pending) return null;

    return (
        <div style={{
            fontSize: "0.8rem",
            color: grey["700"],
            display: "flex",
            alignItems: "center",
        }}>
            <WarningIcon color="warning" fontSize="inherit" />
            <div style={{width: 2}} />
            <div>{pending} un-priced items</div>
        </div>
    )
}